import * as React from "react";
import { useCallback } from "react";
import styled from "styled-components";
import { Icon } from "components/common";
import useCarouselActions from "components/common/Carousel/hooks/useCarouselActions";
import { Button, Alert, Label } from "components/bootstrap";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { EVIDENCE_CAROUSEL_ID } from "security-app/components/Investigations/Details/TimelineSegment/Constants";
import { isPermitted } from "util/PermissionsMixin";
import useCurrentUser from "hooks/useCurrentUser";
import useInvestigationMessagesStreamConfig from "security-app/components/Investigations/Details/TimelineSegment/useInvestigationMessagesStreamConfig";
import useInvestigationEventsStreamConfig from "security-app/components/Investigations/Details/TimelineSegment/useInvestigationEventsStreamConfig";
import EvidenceCarousel from "./EvidenceCarousel";
const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;
const EvidenceCount = styled(Label)`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  margin-left: 0.5rem;
`;
const CarouselWrapper = styled.div`
  display: flex;
  gap: 15px;
  flex: 1;
`;
const NavButton = styled(Button)`
  border-radius: 4px;
  padding: 10px;
  margin-top: 20px;
`;
const StyledUL = styled.ul`
  list-style-type: disc;
  margin: 10px 0;
`;
const navigationTelemetry = (direction) => ({
  app_pathname: "security",
  app_section: "investigation",
  event_details: {
    direction
  }
});
const CarouselNavigation = ({ children }) => {
  const sendTelemetry = useSendTelemetry();
  const { scrollPrev, scrollNext, nextBtnDisabled, prevBtnDisabled } = useCarouselActions(EVIDENCE_CAROUSEL_ID);
  const _scrollPrev = useCallback(() => {
    sendTelemetry(
      TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_TIMELINE_CAROUSEL_NAVIGATED,
      navigationTelemetry("previous")
    );
    scrollPrev();
  }, [scrollPrev, sendTelemetry]);
  const _scrollNext = useCallback(() => {
    sendTelemetry(
      TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_TIMELINE_CAROUSEL_NAVIGATED,
      navigationTelemetry("next")
    );
    scrollNext();
  }, [scrollNext, sendTelemetry]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(NavButton, { onClick: _scrollPrev, disabled: prevBtnDisabled }, /* @__PURE__ */ React.createElement(Icon, { name: "chevron_left" })), children, /* @__PURE__ */ React.createElement(NavButton, { onClick: _scrollNext, disabled: nextBtnDisabled }, /* @__PURE__ */ React.createElement(Icon, { name: "chevron_right" })));
};
const EvidenceOverview = ({
  evidencesGroupedByTime,
  onShowSimilar,
  onResetShowSimilar,
  similarFieldsFilter,
  evidenceCount
}) => {
  const { data: messagesStreamConfig, isInitialLoading: messageStreamLoading } = useInvestigationMessagesStreamConfig();
  const { data: eventsStreamConfig, isInitialLoading: eventStreamLoading } = useInvestigationEventsStreamConfig();
  const currentUser = useCurrentUser();
  if (messageStreamLoading || eventStreamLoading) {
    return /* @__PURE__ */ React.createElement("span", null, "Loading streams...");
  }
  const hasMessageStreamAccess = isPermitted(currentUser.permissions, `streams:read:${messagesStreamConfig.stream_id}`);
  const hasEventsStreamAccess = isPermitted(currentUser.permissions, `streams:read:${eventsStreamConfig.stream_id}`);
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement("h2", null, "Evidence"), /* @__PURE__ */ React.createElement(EvidenceCount, null, evidenceCount)), (!hasMessageStreamAccess || !hasEventsStreamAccess) && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning", title: "Missing Investigation Stream Access" }, "Your user account is missing access to the following streams, which are required to view investigation message and event evidence details. Please contact your administrator and request for them to share these streams with you.", /* @__PURE__ */ React.createElement(StyledUL, null, !hasMessageStreamAccess && /* @__PURE__ */ React.createElement("li", null, "All Investigation messages"), !hasEventsStreamAccess && /* @__PURE__ */ React.createElement("li", null, "All Investigation events"))), !evidencesGroupedByTime.length ? /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, similarFieldsFilter ? "No similarities were found." : "No evidence exists for selected filters.") : /* @__PURE__ */ React.createElement(CarouselWrapper, null, /* @__PURE__ */ React.createElement(CarouselNavigation, null, /* @__PURE__ */ React.createElement(
    EvidenceCarousel,
    {
      evidencesGroupedByTime,
      similarFieldsFilter,
      onResetShowSimilar,
      onShowSimilar,
      hasMessageStreamAccess,
      hasEventsStreamAccess
    }
  ))));
};
export default EvidenceOverview;
